import { FetchResult } from '@apollo/client';
import { Client } from './client';
import { ApiDef } from './apiDef';
import {
  ANNOTATE_ERP,
  GET_USER_ERP,
  REPROCESS_USER_ASSESSMENT
} from '../../src/components/dashboard/data-tools/queries';

const coreApiDefs = new Map<string, ApiDef>();
coreApiDefs.set('GetErpAnnotation', new ApiDef('query', GET_USER_ERP, false));
coreApiDefs.set('AnnotateErp', new ApiDef('mutation', ANNOTATE_ERP, false));

const assessmentApiDefs = new Map<string, ApiDef>();
assessmentApiDefs.set(
  'ReprocessUserAssessment',
  new ApiDef('mutation', REPROCESS_USER_ASSESSMENT, false)
);

const healthcheckApiDefs = new Map<string, ApiDef>();
healthcheckApiDefs.set(
  'GetAppConfig',
  new ApiDef(
    'query',
    `
    query($type: [AppConfigType]!) {
      GetAppConfig(type: $type) {
        key
        value
      }
    }
`,
    true
  )
);

healthcheckApiDefs.set(
  'UpdateAppConfig',
  new ApiDef(
    'mutation',
    `
    mutation(
      $type: AppConfigType!,
      $config: String!
    ) {
      UpdateAppConfig(
        input: {
          type: $type,
          config: $config
        }
      )
    }
`,
    true
  )
);

healthcheckApiDefs.set(
  'HealthCheck',
  new ApiDef(
    'query',
    `
    query($keys: [String]!) {
      HealthCheck(keys: $keys) {
        key
        value
      }
    }
`,
    false
  )
);

healthcheckApiDefs.set(
  'HealthCheckUpdate',
  new ApiDef(
    'mutation',
    `
    mutation UpdateHealthCheckConfigs(
      $items: [UpdateHealthCheckConfigInput]
    ) {
      UpdateHealthCheckConfigs(input: $items)
    }
`,
    false
  )
);

const billingApiDefs = new Map<string, ApiDef>();
billingApiDefs.set(
  'UpdateOrderDispatched',
  new ApiDef(
    'mutation',
    `
    mutation UpdateOrderDispatched(
      $input: UpdateOrderDispatchedInput
    ) {
      UpdateOrderDispatched(input: $input){
        fulfilmentStatus
      }
    }
`,
    false
  )
);

billingApiDefs.set(
  'AdminCancelUserProSubscription',
  new ApiDef(
    'mutation',
    `mutation AdminCancelUserProSubscription(
    $subscriptionId: String!
    $provider: SubscriptionProviderType!
    $metadata: [KeyvalInput]
  ) {
    AdminCancelUserProSubscription(
      input: {
        subscriptionId: $subscriptionId
        provider: $provider
        metadata: $metadata
      }
    ) {
      id
      status
      cancelAtPeriodEnd
    }
  }`,
    false
  )
);

billingApiDefs.set(
  'AdminCancelUserSubscription',
  new ApiDef(
    'mutation',
    `mutation AdminCancelUserSubscription(
    $subscriptionId: String!
    $provider: SubscriptionProviderType!
    $metadata: [KeyvalInput]
  ) {
    AdminCancelUserSubscription(
      input: {
        subscriptionId: $subscriptionId
        provider: $provider
        metadata: $metadata
      }
    ) {
      id
      status
      cancelAtPeriodEnd
    }
  }`,
    false
  )
);

billingApiDefs.set(
  'AdminSubscriptionClearCoupon',
  new ApiDef(
    'mutation',
    `mutation AdminSubscriptionClearCoupon(
      $provider: SubscriptionProviderType!,
      $subscriptionId: String!,
  ) {
    AdminSubscriptionClearCoupon(
      input: {
        provider: $provider,
        subscriptionId: $subscriptionId,
      }
    )
  }`,
    false
  )
);

billingApiDefs.set(
  'ProcessWarrantyEvent',
  new ApiDef(
    'mutation',
    `mutation ProcessWarrantyEvent(
      $orderId: ID!
      $serialNo: String!
      $warrantyCreated: String!
      $warranteedUnitShippedDate: String!
      $reason: String!
      $subReason: String!
      $batchNo: String!
      $unitRetrieved: Boolean!
      $creditStatus: WarrantyCreditStatus!
      $haveSensorsBeenReplaced: Boolean!
      $headsetMAC: String
      $notes: String
  ) {
    ProcessWarrantyEvent(
      input: {
        orderId: $orderId
        serialNo: $serialNo
        headsetMAC: $headsetMAC
        warrantyCreated: $warrantyCreated
        warranteedUnitShippedDate: $warranteedUnitShippedDate
        reason: $reason
        subReason: $subReason
        notes: $notes
        batchNo: $batchNo
        unitRetrieved: $unitRetrieved
        creditStatus: $creditStatus
        haveSensorsBeenReplaced: $haveSensorsBeenReplaced
      }
    ){
      id
      warrantyCreated {
        formatted
      }
      warranteedUnitShippedDate {
        formatted
      }
      serialNo
      reason
      subReason
      notes
      batchNo
      unitRetrieved
      haveSensorsBeenReplaced
      creditStatus
      headset {
        id
        mac
      }
    }
  }`,
    false
  )
);

class ServerInterface {
  clients: Map<string, Client>;

  constructor() {
    this.clients = new Map<string, Client>();
    let uri = process.env.REACT_APP_SENSAI_SERVER_BASE_URL;
    if (!uri || uri === 'localhost' || uri === 'undefined') {
      uri = 'http://localhost:2017/local';
    }

    this.clients.set(
      'healthcheck',
      new Client(`${uri}/healthcheck`, healthcheckApiDefs)
    );

    if (process.env.REACT_APP_SENSAI_SERVER_BASE_URL === 'localhost') {
      uri = 'http://localhost:2003/local';
    }
    this.clients.set('core', new Client(`${uri}/api`, coreApiDefs));

    if (process.env.REACT_APP_SENSAI_SERVER_BASE_URL === 'localhost') {
      uri = 'http://localhost:2002/local';
    }
    this.clients.set('billing', new Client(`${uri}/billing`, billingApiDefs));

    if (process.env.REACT_APP_SENSAI_SERVER_BASE_URL === 'localhost') {
      uri = 'http://localhost:2017/local';
    }
    this.clients.set(
      'assessment',
      new Client(`${uri}/assessment`, assessmentApiDefs)
    );
  }

  getClientDef(endPointKey: string): Client | null {
    const c = this.clients.get(endPointKey);
    if (c === null || c === undefined) {
      return null;
    }
    return c;
  }

  async execute(
    endPointKey: string,
    apiKey: string,
    variables: Record<string, unknown>
  ): Promise<FetchResult | null> {
    const c = this.clients.get(endPointKey);
    if (!c) {
      return null;
    }
    return c.execute(apiKey, variables);
  }
}

const API = new ServerInterface();

export default API;
